import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, ChevronDown, Loader2 } from 'lucide-react';
import { createStandardQuestion, updateStandardQuestion, fetchSQByTaskId } from '../../../slice/sqSlice';
import { selectSelectedTask } from '../../../slice/taskSlice';
import { useThemeColor } from '../../../hooks/useThemeColor';
import { useTranslation } from '../../../i18n/LanguageContext';

const AddQuestionModal = ({ isOpen, onClose, selectedQuestion, categories: categoriesProp = [] }) => {
  const { aispaceColor } = useThemeColor();
  const { t } = useTranslation();
  const { data } = useSelector((state) => state.admin);

  const isAdmin = data?.user_role === "SUPER_ADMIN" || data?.user_role === "ADMIN";
  const isSuperAdmin = data?.user_role === "SUPER_ADMIN" || data?.user_role === "ADMIN";
  const userOrganizationId = data?.organization_id;
  const dispatch = useDispatch();
  const selectedTask = useSelector(selectSelectedTask);

  const [formData, setFormData] = useState({
    title: '',
    category_id: null,
    description: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const catDropdownRef = useRef(null);

  // Close custom dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (catDropdownRef.current && !catDropdownRef.current.contains(e.target)) setCatDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Category options from props (fetched from DB)
  const categories = categoriesProp;

  useEffect(() => {
    if (selectedQuestion) {
      setFormData({
        title: selectedQuestion.title || '',
        category_id: selectedQuestion.category_id || null,
        description: selectedQuestion.description || ''
      });
    } else {
      clearForm();
    }
  }, [selectedQuestion]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const clearForm = () => {
    setFormData({
      title: '',
      category_id: null,
      description: ''
    });
    setError('');
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError(t('agentSettings.questionEmptyTitleError'));
      return;
    }

    if (!formData.description.trim()) {
      setError(t('agentSettings.questionEmptyDescriptionError'));
      return;
    }

    // Check permissions for ADMIN users
    if (isAdmin && !isSuperAdmin) {
      if (selectedTask.organization_id && selectedTask.organization_id !== userOrganizationId) {
        setError(t('agentSettings.questionOrgPermissionError'));
        return;
      }
    }

    setIsLoading(true);
    setError('');

    try {
      if (selectedQuestion) {
        // Update existing question
        await dispatch(updateStandardQuestion({
          id: selectedQuestion.id,
          ...formData
        })).unwrap();
      } else {
        // Create new question
        await dispatch(createStandardQuestion({
          ...formData,
          task_id: selectedTask.id
        })).unwrap();

        clearForm();
      }

      // Refetch questions to update the list
      await dispatch(fetchSQByTaskId(selectedTask.id));
      onClose();
    } catch (err) {
      setError(err.message || t('agentSettings.questionGenericError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    clearForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center pt-20 p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-[560px] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-[#E5E7EB]">
          <h2 className="text-[20px] font-bold text-[#111827]">
            {selectedQuestion ? t('agentSettings.editQuestionTitle') : t('agentSettings.addQuestionTitle')}
          </h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors"
          >
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Form Content */}
        <div className="px-8 py-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-[14px] text-red-600">
              {error}
            </div>
          )}

          {/* Question Title */}
          <div>
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">
              {t('agentSettings.questionTitleLabel')}
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              disabled={!isAdmin}
              placeholder={t('agentSettings.questionTitlePlaceholder')}
              className="w-full px-4 py-3 text-[14px] text-[#374151] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed placeholder:text-[#9CA3AF]"
              style={{ '--focus-color': aispaceColor }}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">
              {t('agentSettings.questionCategoryLabel')}
            </label>
            <div className="relative" ref={catDropdownRef}>
              <button
                type="button"
                onClick={() => { if (isAdmin) setCatDropdownOpen(!catDropdownOpen); }}
                disabled={!isAdmin}
                className="w-full px-4 py-3 pr-10 text-[14px] text-left border rounded-xl bg-white focus:outline-none cursor-pointer disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
                style={{ borderColor: catDropdownOpen ? aispaceColor : '#E5E7EB' }}
              >
                <span className={formData.category_id ? 'text-[#374151]' : 'text-[#9CA3AF]'}>
                  {formData.category_id ? categories.find(c => c.id === formData.category_id)?.category_name : t('agentSettings.questionCategoryPlaceholder')}
                </span>
              </button>
              <ChevronDown size={18} className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#6B7280] transition-transform ${catDropdownOpen ? 'rotate-180' : ''}`} />
              {catDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E7EB] rounded-xl shadow-lg max-h-[200px] overflow-y-auto">
                  <div
                    onClick={() => { setFormData(prev => ({ ...prev, category_id: null })); setCatDropdownOpen(false); }}
                    className="px-4 py-2.5 text-[14px] text-[#9CA3AF] hover:bg-[#F9FAFB] cursor-pointer rounded-t-xl"
                  >
                    {t('agentSettings.questionCategoryPlaceholder')}
                  </div>
                  {categories.map((cat) => (
                    <div
                      key={cat.id}
                      onClick={() => { setFormData(prev => ({ ...prev, category_id: cat.id })); setCatDropdownOpen(false); }}
                      className={`px-4 py-2.5 text-[14px] cursor-pointer transition-colors ${
                        formData.category_id === cat.id ? 'font-medium' : 'text-[#374151] hover:bg-[#F9FAFB]'
                      }`}
                      style={formData.category_id === cat.id ? { backgroundColor: `${aispaceColor}10`, color: aispaceColor } : {}}
                    >
                      {cat.category_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">
              {t('agentSettings.questionDescriptionLabel')}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={t('agentSettings.questionDescriptionPlaceholder')}
              disabled={!isAdmin}
              rows={8}
              className="w-full px-4 py-3 text-[14px] text-[#374151] border border-[#E5E7EB] rounded-xl resize-none focus:outline-none focus:border-[var(--focus-color)] transition-colors disabled:bg-[#F9FAFB] disabled:cursor-not-allowed placeholder:text-[#9CA3AF]"
              style={{ '--focus-color': aispaceColor }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 bg-[#F9FAFB] border-t border-[#E5E7EB] flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-6 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] transition-colors"
          >
            {t('agentSettings.cancel')}
          </button>
          <button
            disabled={!isAdmin || isLoading}
            onClick={handleSubmit}
            className="px-6 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[160px] justify-center"
            style={{ backgroundColor: aispaceColor }}
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>{t('agentSettings.saving')}</span>
              </>
            ) : (
              <span>{selectedQuestion ? t('agentSettings.updateQuestionConfirm') : t('agentSettings.addQuestionConfirm')}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddQuestionModal;
